import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { updateModuleProgress } from '../../../SkillsApi/SkillsApi'
import './AiModuleOne.css'

const MODULE_ID = 1
const SECTION = 'AiSection'
// TODO: swap for the actual AI Module 1 video URL
const VIDEO_URL = 'https://upskld-demo-videos.s3.ap-south-1.amazonaws.com/AI_Course_Module_1.mp4'

const ASSESSMENT_QUESTIONS = [
  {
    id: 'q1',
    question: 'Machine Learning-ல "supervised learning"-ஐ எப்படி define பண்றாங்க?',
    options: [
      { key: 'a', label: 'Training with labeled input-output pairs' },
      { key: 'b', label: 'Training with no data at all' },
      { key: 'c', label: 'Random guessing without feedback' },
      { key: 'd', label: 'Manual rule-based programming only' },
    ],
    correctKey: 'a',
  },
  {
    id: 'q2',
    question: 'Neural network-ல "overfitting" problem-ஐ குறைக்க பொதுவா எது use பண்றாங்க?',
    options: [
      { key: 'a', label: 'Regularization / dropout' },
      { key: 'b', label: 'Increasing screen brightness' },
      { key: 'c', label: 'Removing all training data' },
      { key: 'd', label: 'Bigger font size in code' },
    ],
    correctKey: 'a',
  },
  {
    id: 'q3',
    question: 'Large Language Models (LLMs)-ல "hallucination" எதை குறிக்கிறது?',
    options: [
      { key: 'a', label: 'Model generating incorrect or made-up information confidently' },
      { key: 'b', label: 'A hardware overheating issue' },
      { key: 'c', label: 'A network connectivity error' },
      { key: 'd', label: 'A type of data compression' },
    ],
    correctKey: 'a',
  },
]

const AiModuleOne = () => {
  const navigate = useNavigate()
  const [answers, setAnswers] = useState({})
  const [watchPercent, setWatchPercent] = useState(0)
  const [videoEnded, setVideoEnded] = useState(false)
  const [finishing, setFinishing] = useState(false)

  const lastSentMilestoneRef = useRef(0)

  useEffect(() => {
    document.querySelector('#root')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Same right-click / DevTools-shortcut deterrent as the drone course modules.
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault()
      toast.warn('Right-click is disabled on this page.')
    }

    const handleKeyDown = (e) => {
      const isDevToolsShortcut =
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C', 'i', 'j', 'c'].includes(e.key)) ||
        (e.ctrlKey && ['U', 'u'].includes(e.key))

      if (isDevToolsShortcut) {
        e.preventDefault()
        toast.warn('Inspect is disabled on this page.')
      }
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const sendMilestone = async (milestone) => {
    if (milestone <= lastSentMilestoneRef.current) return
    lastSentMilestoneRef.current = milestone
    await updateModuleProgress(SECTION, MODULE_ID, milestone)
  }

  // Milestones capped at 90 during playback — the final 100% is only sent
  // once the assessment is finished too (see handleFinish), so AI Module 2
  // only unlocks once BOTH the video and the quiz are done.
  const handleTimeUpdate = (e) => {
    const { currentTime, duration } = e.target
    if (!duration) return

    const percent = Math.min(100, Math.floor((currentTime / duration) * 100))
    setWatchPercent((prev) => (percent > prev ? percent : prev))

    const crossedMilestone = Math.min(90, Math.floor(percent / 10) * 10)
    if (crossedMilestone > lastSentMilestoneRef.current) {
      sendMilestone(crossedMilestone)
    }
  }

  const handleVideoEnded = () => {
    setWatchPercent(100)
    setVideoEnded(true)
    toast.info('Video watched! Now complete the assessment below.')
  }

  const handleAnswerSelect = (questionId, optionKey, correctKey) => {
    if (answers[questionId]) return

    setAnswers((prev) => ({ ...prev, [questionId]: optionKey }))

    if (optionKey === correctKey) {
      toast.success('Correct answer!')
    } else {
      toast.error('Wrong answer!')
    }
  }

  const allQuestionsAnswered = ASSESSMENT_QUESTIONS.every((q) => answers[q.id])
  const canFinish = videoEnded && allQuestionsAnswered

  const handleFinish = async () => {
    if (!canFinish || finishing) return
    setFinishing(true)
    await sendMilestone(100)
    toast.success('Module 1 complete — Module 2 unlocked!')
    navigate('/ai_course')
  }

  return (
    <div className="AiModuleOne_wrapper">
      <ToastContainer position="top-right" autoClose={2000} />

      <div className="AiModuleOne_content">
        <h2 className="AiModuleOne_title">Module 1: Deep Dive</h2>
        <p className="AiModuleOne_subtitle">
          Watch the full video, then complete the short assessment to unlock Module 2.
        </p>

        <div className="AiModuleOne_video_wrap">
          <video
            className="AiModuleOne_video"
            src={VIDEO_URL}
            controls
            controlsList="nodownload"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        </div>

        <div className="AiModuleOne_progress_row">
          <div className="AiModuleOne_progress_track">
            <div
              className="AiModuleOne_progress_fill"
              style={{ width: `${watchPercent}%` }}
            />
          </div>
          <span className="AiModuleOne_progress_label">{watchPercent}% watched</span>
        </div>

        <div className="AiModuleOne_assessment">
          <h3 className="AiModuleOne_assessment_title">Module Assessment</h3>
          <p className="AiModuleOne_assessment_sub">
            {videoEnded
              ? `Answer all ${ASSESSMENT_QUESTIONS.length} questions to complete this module.`
              : 'Finish watching the video above to unlock the assessment.'}
          </p>

          {ASSESSMENT_QUESTIONS.map((q, qIndex) => {
            const selected = answers[q.id]

            return (
              <div className="AiModuleOne_question_card" key={q.id}>
                <p className="AiModuleOne_question_text">
                  <span className="AiModuleOne_question_num">Q{qIndex + 1}.</span> {q.question}
                </p>

                <div className="AiModuleOne_options_grid">
                  {q.options.map((opt) => {
                    const isSelected = selected === opt.key
                    const isCorrectOpt = opt.key === q.correctKey
                    let stateClass = ''

                    if (selected) {
                      if (isSelected && isCorrectOpt) stateClass = 'AiModuleOne_option_correct'
                      else if (isSelected && !isCorrectOpt) stateClass = 'AiModuleOne_option_wrong'
                      else if (!isSelected && isCorrectOpt) stateClass = 'AiModuleOne_option_reveal'
                    }

                    return (
                      <button
                        type="button"
                        key={opt.key}
                        className={`AiModuleOne_option_btn ${stateClass}`}
                        onClick={() => handleAnswerSelect(q.id, opt.key, q.correctKey)}
                        disabled={!videoEnded || !!selected}
                      >
                        <span className="AiModuleOne_option_key">{opt.key.toUpperCase()}</span>
                        <span className="AiModuleOne_option_label">{opt.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}

          <button
            type="button"
            className="AiModuleOne_complete_btn"
            onClick={handleFinish}
            disabled={!canFinish || finishing}
          >
            {finishing ? 'Saving...' : (
              <>
                Complete Module <span className="AiModuleOne_complete_check">&#10003;</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AiModuleOne